(() => {
  if (window.rasFreebiePickerReady) return;
  window.rasFreebiePickerReady = true;

  const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  const sections = ['cart-drawer', 'cart-icon-bubble'];
  const discountStorageKey = 'rasSelectedRewardDiscountCode';

  const fetchJson = async (url, options = {}) => {
    const response = await fetch(url, {
      ...options,
      headers: {
        Accept: 'application/json',
        ...(options.headers || {})
      }
    });

    let data = null;

    try {
      data = await response.json();
    } catch (error) {
      data = null;
    }

    if (!response.ok) {
      const message = data && (data.description || data.message || data.status);
      throw new Error(message || `Request failed: ${url}`);
    }

    return data;
  };

  const fetchCart = () => fetchJson(root + 'cart.js');

  const idsFromPicker = (picker) => String(picker.dataset.variantIds || '')
    .split(',')
    .map((id) => Number(id.trim()))
    .filter(Boolean);

  const isFreebie = (item, ids) => ids.includes(Number(item.variant_id));

  const paidTotal = (cart, ids) => cart.items.reduce((total, item) => {
    if (isFreebie(item, ids)) return total;

    const price = item.final_line_price ?? item.line_price ?? item.original_line_price ?? 0;
    return total + Number(price);
  }, 0);

  const selectedDiscountCode = () => window.localStorage.getItem(discountStorageKey) || '';

  const setSelectedDiscountCode = (code) => {
    if (code) {
      window.localStorage.setItem(discountStorageKey, code);
    } else {
      window.localStorage.removeItem(discountStorageKey);
    }
  };

  const updateDiscountVisual = () => {
    const code = selectedDiscountCode();

    document.querySelectorAll('[data-discount-option]').forEach((button) => {
      const buttonCode = button.dataset.discountCode || '';
      const locked = button.getAttribute('aria-disabled') === 'true';
      const selected = Boolean(code && buttonCode === code && !locked);

      button.classList.toggle('is-selected', selected);
      button.setAttribute('aria-pressed', selected ? 'true' : 'false');
    });
  };

  const applyDiscountToCheckout = () => {
    const code = selectedDiscountCode();

    document.querySelectorAll('cart-drawer form[action*="/cart"], #CartDrawer-Form').forEach((form) => {
      let input = form.querySelector('input[name="discount"][data-ras-discount-input]');

      if (!input && code) {
        input = document.createElement('input');
        input.type = 'hidden';
        input.name = 'discount';
        input.setAttribute('data-ras-discount-input', 'true');
        form.appendChild(input);
      }

      if (input) {
        input.value = code;

        if (!code) {
          input.remove();
        }
      }
    });

    document.querySelectorAll('cart-drawer a[href*="/checkout"], #CartDrawer-Form a[href*="/checkout"]').forEach((link) => {
      link.href = code
        ? root + 'discount/' + encodeURIComponent(code) + '?redirect=/checkout'
        : root + 'checkout';
    });
  };

  const renderDrawer = (state) => {
    if (!state || !state.sections) return;

    const drawer = document.querySelector('cart-drawer');

    if (drawer && typeof drawer.renderContents === 'function') {
      drawer.renderContents(state);

      if (typeof state.item_count === 'number') {
        drawer.classList.toggle('is-empty', state.item_count === 0);
      }

      window.setTimeout(() => {
        updateDiscountVisual();
        applyDiscountToCheckout();
      }, 50);
    }
  };

  const updateCartReward = (picker, selectedFreebieId = null, discountCode = '') => {
    const ids = idsFromPicker(picker);
    const updates = {};

    ids.forEach((id) => {
      updates[id] = 0;
    });

    if (selectedFreebieId) {
      updates[selectedFreebieId] = 1;
    }

    return fetchJson(root + 'cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        updates,
        discount: discountCode || '',
        sections,
        sections_url: window.location.pathname
      })
    });
  };

  const updateButtonsVisual = (picker, selectedId = null, lockUnavailableOnly = true) => {
    picker.querySelectorAll('[data-freebie-option]').forEach((button) => {
      const id = Number(button.dataset.variantId);
      const available = button.dataset.variantAvailable === 'true';
      const selected = selectedId && id === Number(selectedId);
      const locked = lockUnavailableOnly ? !available : true;

      button.classList.toggle('is-selected', Boolean(selected));
      button.classList.toggle('is-locked', locked);
      button.setAttribute('aria-disabled', locked ? 'true' : 'false');
    });
  };

  const updateButtonsFromCart = (picker, cart) => {
    const ids = idsFromPicker(picker);
    const threshold = Number(picker.dataset.threshold || 139000);
    const eligible = paidTotal(cart, ids) >= threshold;

    const selectedIds = cart.items
      .filter((item) => isFreebie(item, ids))
      .map((item) => Number(item.variant_id));

    picker.querySelectorAll('[data-freebie-option]').forEach((button) => {
      const id = Number(button.dataset.variantId);
      const available = button.dataset.variantAvailable === 'true';
      const selected = selectedIds.includes(id);
      const locked = !eligible || !available;

      button.classList.toggle('is-selected', selected);
      button.classList.toggle('is-locked', locked);
      button.setAttribute('aria-disabled', locked ? 'true' : 'false');
    });

    picker.querySelectorAll('[data-discount-option]').forEach((button) => {
      const locked = !eligible;
      button.classList.toggle('is-locked', locked);
      button.setAttribute('aria-disabled', locked ? 'true' : 'false');

      if (locked && button.classList.contains('is-selected')) {
        setSelectedDiscountCode('');
      }
    });

    updateDiscountVisual();
    applyDiscountToCheckout();
  };

  const updatePickerState = async () => {
    const picker = document.querySelector('cart-freebie-picker');
    if (!picker) return;

    const cart = await fetchCart();
    updateButtonsFromCart(picker, cart);
  };

  window.rasFreebieChoose = async (button) => {
    const picker = button.closest('cart-freebie-picker');
    if (!picker || picker.classList.contains('is-loading')) return;

    const id = Number(button.dataset.variantId);
    const available = button.dataset.variantAvailable === 'true';
    const locked = button.getAttribute('aria-disabled') === 'true';

    if (!id || !available || locked) return;

    const wasSelected = button.classList.contains('is-selected');
    const previousSelected = picker.querySelector('[data-freebie-option].is-selected');

    picker.classList.add('is-loading');

    try {
      const cart = await fetchCart();
      const ids = idsFromPicker(picker);
      const threshold = Number(picker.dataset.threshold || 139000);

      if (!wasSelected && paidTotal(cart, ids) < threshold) {
        updateButtonsFromCart(picker, cart);
        return;
      }

      setSelectedDiscountCode('');
      updateDiscountVisual();
      applyDiscountToCheckout();

      if (wasSelected) {
        updateButtonsVisual(picker, null, false);
      } else {
        updateButtonsVisual(picker, id, false);
      }

      const state = await updateCartReward(picker, wasSelected ? null : id, '');
      renderDrawer(state);
    } catch (error) {
      console.error('Freebie choose error:', error);

      if (previousSelected) {
        updateButtonsVisual(picker, Number(previousSelected.dataset.variantId));
      } else {
        updateButtonsVisual(picker, null);
      }
    } finally {
      picker.classList.remove('is-loading');

      window.setTimeout(() => {
        updatePickerState().catch((error) => {
          console.error('Freebie picker update error:', error);
        });
      }, 250);
    }
  };

  let cleanupTimer = null;
  let cleanupRunning = false;

  const cleanupIneligibleFreebie = async () => {
    if (cleanupRunning) return;

    const picker = document.querySelector('cart-freebie-picker');
    if (!picker) return;

    const ids = idsFromPicker(picker);
    const threshold = Number(picker.dataset.threshold || 139000);

    if (!ids.length) return;

    cleanupRunning = true;

    try {
      const cart = await fetchCart();

      if (paidTotal(cart, ids) < threshold) {
        const hasFreebie = cart.items.some((item) => isFreebie(item, ids));
        const hasSelectedDiscount = Boolean(selectedDiscountCode());

        setSelectedDiscountCode('');
        updateDiscountVisual();
        applyDiscountToCheckout();

        if (!hasFreebie && !hasSelectedDiscount) {
          updateButtonsFromCart(picker, cart);
          return;
        }

        const updates = {};

        cart.items.forEach((item) => {
          if (isFreebie(item, ids)) {
            updates[item.key] = 0;
          }
        });

        const state = await fetchJson(root + 'cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            updates,
            discount: '',
            sections,
            sections_url: window.location.pathname
          })
        });

        renderDrawer(state);
      } else {
        updateButtonsFromCart(picker, cart);
      }
    } catch (error) {
      console.error('Freebie cleanup error:', error);
    } finally {
      cleanupRunning = false;
    }
  };

  const queueCleanup = (delay = 300) => {
    window.clearTimeout(cleanupTimer);
    cleanupTimer = window.setTimeout(cleanupIneligibleFreebie, delay);
  };

  if (!window.rasFreebieFetchPatched) {
    window.rasFreebieFetchPatched = true;

    const originalFetch = window.fetch.bind(window);

    window.fetch = async (...args) => {
      const response = await originalFetch(...args);
      const requestUrl = String(args[0] && args[0].url ? args[0].url : args[0]);

      if (
        requestUrl.includes('/cart/change') ||
        requestUrl.includes('/cart/update') ||
        requestUrl.includes('/cart/add')
      ) {
        queueCleanup(350);
      }

      return response;
    };
  }

  document.addEventListener('click', (event) => {
    const button = event.target.closest('[data-freebie-option]');
    if (!button || !button.closest('cart-freebie-picker')) return;

    event.preventDefault();
    event.stopPropagation();

    window.rasFreebieChoose(button);
  });

  document.addEventListener('click', async (event) => {
    const button = event.target.closest('[data-discount-option]');
    if (!button || !button.closest('cart-freebie-picker')) return;

    event.preventDefault();
    event.stopPropagation();

    const picker = button.closest('cart-freebie-picker');
    if (!picker || picker.classList.contains('is-loading')) return;

    const locked = button.getAttribute('aria-disabled') === 'true';
    const code = button.dataset.discountCode || '';

    if (!code || locked) return;

    const alreadySelected = button.classList.contains('is-selected');

    picker.classList.add('is-loading');

    try {
      const cart = await fetchCart();
      const ids = idsFromPicker(picker);
      const threshold = Number(picker.dataset.threshold || 139000);

      if (!alreadySelected && paidTotal(cart, ids) < threshold) {
        updateButtonsFromCart(picker, cart);
        return;
      }

      if (alreadySelected) {
        setSelectedDiscountCode('');
        updateButtonsVisual(picker, null);
        updateDiscountVisual();
        applyDiscountToCheckout();

        const state = await updateCartReward(picker, null, '');
        renderDrawer(state);
      } else {
        setSelectedDiscountCode(code);
        updateButtonsVisual(picker, null);
        updateDiscountVisual();
        applyDiscountToCheckout();

        const state = await updateCartReward(picker, null, code);
        renderDrawer(state);
      }
    } catch (error) {
      console.error('Discount reward error:', error);
    } finally {
      picker.classList.remove('is-loading');

      window.setTimeout(() => {
        updatePickerState().catch((error) => {
          console.error('Freebie picker update error:', error);
        });
      }, 250);
    }
  });

  document.addEventListener('change', (event) => {
    if (event.target.closest('#CartDrawer-Form')) queueCleanup(300);
  });

  document.addEventListener('click', (event) => {
    if (
      event.target.closest(
        '#CartDrawer-Form .quantity__button, #CartDrawer-Form cart-remove-button'
      )
    ) {
      queueCleanup(300);
    }
  });

  setSelectedDiscountCode('');
  updateDiscountVisual();
  applyDiscountToCheckout();
  queueCleanup(300);
})();






